import React, { useEffect, useState } from 'react';
import { Newspaper, Globe, ExternalLink, X, Mic2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, fileUrl } from '@/lib/api';

const initials = (name) => (name || '')
  .split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]).join('').toUpperCase();

const normalizeUrl = (u) => {
  if (!u) return '';
  return u.startsWith('http') ? u : `https://${u}`;
};

const parseSocialLinks = (raw) => {
  if (!raw) return [];
  return raw.split(/[,\n;|]+/).map((s) => s.trim()).filter(Boolean).slice(0, 6);
};

const Chip = ({ children, className = '' }) => (
  <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${className}`}>
    {children}
  </span>
);

const MediaCard = ({ item, onReadMore }) => (
  <div
    data-testid={`media-card-${item.id}`}
    className="group bg-white rounded-2xl p-5 border border-aawta-border hover:shadow-aawta-elevated hover:-translate-y-1 transition-all duration-300 flex flex-col"
  >
    <div className="h-28 rounded-xl bg-aawta-surface border border-aawta-border flex items-center justify-center overflow-hidden">
      {item.logo_file_id ? (
        <img src={fileUrl(item.logo_file_id)} alt={item.outlet || item.full_name}
             className="max-h-24 max-w-[85%] object-contain" loading="lazy" />
      ) : (
        <div className="text-aawta-navy font-display font-bold text-3xl tracking-wide">
          {initials(item.outlet || item.full_name)}
        </div>
      )}
    </div>

    <div className="mt-4 flex-1 flex flex-col">
      <div className="font-display font-semibold text-aawta-navy text-[15.5px] leading-snug">
        {item.outlet || item.full_name}
      </div>
      {item.outlet && item.full_name && (
        <div className="text-[12.5px] text-aawta-muted mt-0.5 truncate">
          {item.full_name}{item.job_title ? ` · ${item.job_title}` : ''}
        </div>
      )}

      {(item.coverage_type || item.assigned_type) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {item.coverage_type && (
            <Chip className="bg-aawta-magenta/10 text-aawta-magenta">{item.coverage_type}</Chip>
          )}
          {item.assigned_type && item.assigned_type !== item.coverage_type && (
            <Chip className="bg-aawta-navy/5 text-aawta-navy">{item.assigned_type}</Chip>
          )}
        </div>
      )}

      {item.coverage_plans && (
        <p className="mt-3 text-[13.5px] leading-relaxed text-aawta-muted line-clamp-3">
          {item.coverage_plans}
        </p>
      )}
    </div>

    <div className="mt-4 pt-4 border-t border-aawta-border flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => onReadMore(item)}
        data-testid={`media-read-more-${item.id}`}
        className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-aawta-navy hover:text-aawta-magenta"
      >
        Read more <ArrowRight className="h-3.5 w-3.5" />
      </button>
      {item.website && (
        <a
          href={normalizeUrl(item.website)}
          target="_blank" rel="noopener noreferrer"
          data-testid={`media-visit-${item.id}`}
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-aawta-magenta hover:text-aawta-magenta-hover"
        >
          <Globe className="h-3.5 w-3.5" /> Visit
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  </div>
);

const MediaModal = ({ item, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!item) return null;
  const socials = parseSocialLinks(item.social_media);
  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="media-modal"
      onClick={onClose}
      className="fixed inset-0 z-[80] bg-aawta-navy-deep/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-aawta-elevated border border-aawta-border"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-white border-b border-aawta-border">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-aawta-surface border border-aawta-border flex items-center justify-center overflow-hidden shrink-0">
              {item.logo_file_id ? (
                <img src={fileUrl(item.logo_file_id)} alt="" className="max-h-9 max-w-[85%] object-contain" />
              ) : (
                <div className="text-aawta-navy font-display font-bold text-lg">{initials(item.outlet || item.full_name)}</div>
              )}
            </div>
            <div className="min-w-0">
              <div className="font-display font-semibold text-aawta-navy text-[16px] leading-tight truncate">
                {item.outlet || item.full_name}
              </div>
              {item.full_name && (
                <div className="text-[12.5px] text-aawta-muted truncate">
                  {item.full_name}{item.job_title ? ` · ${item.job_title}` : ''}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="media-modal-close"
            className="w-9 h-9 rounded-full hover:bg-aawta-surface flex items-center justify-center text-aawta-muted hover:text-aawta-navy shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {(item.coverage_type || item.assigned_type || item.estimated_reach) && (
            <div className="flex flex-wrap gap-1.5">
              {item.coverage_type && <Chip className="bg-aawta-magenta/10 text-aawta-magenta">{item.coverage_type}</Chip>}
              {item.assigned_type && item.assigned_type !== item.coverage_type && (
                <Chip className="bg-aawta-navy/5 text-aawta-navy">{item.assigned_type}</Chip>
              )}
              {item.estimated_reach && (
                <Chip className="bg-emerald-50 text-emerald-700">Reach · {item.estimated_reach}</Chip>
              )}
            </div>
          )}

          {item.coverage_plans && (
            <div>
              <div className="text-[10.5px] font-bold uppercase tracking-wider text-aawta-muted">About &amp; Coverage Focus</div>
              <p className="mt-2 text-[14.5px] leading-relaxed text-aawta-navy whitespace-pre-wrap">
                {item.coverage_plans}
              </p>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            {item.website && (
              <a
                href={normalizeUrl(item.website)}
                target="_blank" rel="noopener noreferrer"
                className="p-3 rounded-xl bg-aawta-surface border border-aawta-border hover:border-aawta-magenta/40 flex items-center gap-2 text-[13.5px] text-aawta-navy font-medium"
              >
                <Globe className="h-4 w-4 text-aawta-magenta" />
                <span className="truncate">{item.website.replace(/^https?:\/\//, '')}</span>
                <ExternalLink className="h-3.5 w-3.5 ml-auto text-aawta-muted" />
              </a>
            )}
            {socials.map((s, i) => (
              <a
                key={i}
                href={normalizeUrl(s)}
                target="_blank" rel="noopener noreferrer"
                className="p-3 rounded-xl bg-aawta-surface border border-aawta-border hover:border-aawta-magenta/40 flex items-center gap-2 text-[13.5px] text-aawta-navy font-medium"
              >
                <Mic2 className="h-4 w-4 text-aawta-magenta" />
                <span className="truncate">{s.replace(/^https?:\/\//, '').replace(/^www\./, '')}</span>
                <ExternalLink className="h-3.5 w-3.5 ml-auto text-aawta-muted" />
              </a>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 px-6 py-4 bg-white border-t border-aawta-border flex justify-end">
          <Button onClick={onClose} data-testid="media-modal-close-btn" variant="outline" className="rounded-full border-aawta-border">
            Read Less
          </Button>
        </div>
      </div>
    </div>
  );
};

export const MeetOurMedia = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/public/media');
        setItems(data || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <section id="media" data-testid="media-section" className="py-24 md:py-32 bg-aawta-surface">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="w-11 h-11 rounded-xl bg-aawta-magenta text-white flex items-center justify-center">
              <Newspaper className="h-5 w-5" />
            </div>
            <span className="overline text-aawta-magenta mt-4 block">Media &amp; Press</span>
            <h2 className="font-display font-bold text-4xl md:text-5xl mt-3 text-aawta-navy tracking-tight leading-[1.1]">
              Meet Our Approved Media
            </h2>
            <div className="divider-magenta mt-6" />
            <p className="mt-6 text-[17px] leading-relaxed text-aawta-muted">
              The outlets, journalists, producers, and creators approved to cover the AAWTA
              Building the Future Workforce Summit. Every profile below reflects an approved
              Media application — apply for credentials to be featured here.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="/media"
                data-testid="media-apply-cta"
                className="inline-flex items-center gap-2 rounded-full bg-aawta-magenta hover:bg-aawta-magenta-hover text-white font-medium px-6 py-3 shadow-aawta-magenta transition-all hover:-translate-y-0.5"
              >
                Apply for Media Credentials <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            {loading ? (
              <div className="text-center text-aawta-muted py-12">Loading approved media…</div>
            ) : items.length === 0 ? (
              <div
                data-testid="media-empty"
                className="rounded-2xl bg-white border border-dashed border-aawta-border p-8 text-center"
              >
                <Newspaper className="h-8 w-8 text-aawta-magenta mx-auto" />
                <div className="mt-3 font-display font-semibold text-aawta-navy text-lg">Approved media coming soon</div>
                <p className="mt-1 text-[14px] text-aawta-muted max-w-md mx-auto">
                  As media applications are approved, credentialed outlets will be featured here.
                  If you cover workforce development, we&apos;d love to hear from you.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {items.map((it) => <MediaCard key={it.id} item={it} onReadMore={setActive} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      <MediaModal item={active} onClose={() => setActive(null)} />
    </section>
  );
};

export default MeetOurMedia;
